<script setup lang="ts">
import { ref, computed, watch, onMounted, onUnmounted } from 'vue'
import { useRouter } from 'vue-router'
import AdminBrandForm from '@/views/forms/AdminBrandForm.vue'
import AdminBrandDetail from '@/views/AdminBrandDetail.vue'
import BrandsTable from '@/components/BrandsTable.vue'
import TriStateFilterButtons from '@/components/TriStateFilterButtons.vue'
import { useAdmin } from '@/stores/admin'
import { useImports, type PlatformImportStats } from '@/stores/imports'
import type { Brand, PlatformAdvertiser } from '@/stores/admin'
import { handleError } from '@/lib/error'
import { formatPlatformName } from '@/lib/utils'
import { useTriStateFilter, NO_SECTOR } from '@/composables/useTriStateFilter'
import { useUrlFilters } from '@/composables/useUrlFilters'
import { activeFilterUi, sortSelectedFirst } from '@/lib/filterUtils'

const router = useRouter()
const admin = useAdmin()
const imports = useImports()

// Loading state
const isLoading = ref(true)

// Advertisers and import stats maps
const brandAdvertisersMap = ref<Map<string, PlatformAdvertiser[]>>(new Map())
const brandImportStatsMap = ref<Map<string, PlatformImportStats[]>>(new Map())

// Data loaded flags (to avoid re-fetching)
const advertisersLoaded = ref(false)
const importStatsLoaded = ref(false)

// Search and filter
const searchQuery = ref('')
const platformFilter = useTriStateFilter()
const sectorFilter = useTriStateFilter()
const selectedClients = ref<string[]>([])

// URL filters
useUrlFilters({
  filters: { search: searchQuery, clients: selectedClients },
  triStateFilters: {
    platform: platformFilter,
    sector: sectorFilter,
  },
  debounce: 300,
})

// Modals and slideovers
const showCreateBrandModal = ref(false)
const showBrandSlideover = ref(false)

// Selected brand for editing
const selectedBrand = ref<Brand | null>(null)

// Computed properties
const rootPlatforms = computed(() => admin.platforms.filter((p) => !p.parent_platform_id))

const filteredBrands = computed(() => {
  let result = admin.brands

  // Filter by search query
  if (searchQuery.value) {
    const query = searchQuery.value.toLowerCase()
    result = result.filter((b) => b.name.toLowerCase().includes(query))
  }

  // Filter by included platforms (AND - must have ALL selected, checking subplatforms)
  if (platformFilter.included.value.length > 0) {
    result = result.filter((b) =>
      platformFilter.included.value.every((rootCode) => {
        const familyCodes = admin.getPlatformFamilyCodes(rootCode)
        return familyCodes.some((code) => b.platform_codes.includes(code))
      }),
    )
  }

  // Filter by excluded platforms (AND - must be missing ALL excluded, including subplatforms)
  if (platformFilter.excluded.value.length > 0) {
    result = result.filter((b) =>
      platformFilter.excluded.value.every((rootCode) => {
        const familyCodes = admin.getPlatformFamilyCodes(rootCode)
        return !familyCodes.some((code) => b.platform_codes.includes(code))
      }),
    )
  }

  // Filter by included sectors (OR - must have ANY selected)
  if (sectorFilter.included.value.length > 0) {
    result = result.filter((b) => {
      const brandSectorIds = b.sectors.map((s) => s.id)
      return sectorFilter.included.value.some((sectorId) => {
        if (sectorId === NO_SECTOR) return b.sectors.length === 0
        return brandSectorIds.includes(sectorId)
      })
    })
  }

  // Filter by excluded sectors (AND - must NOT have ANY excluded)
  if (sectorFilter.excluded.value.length > 0) {
    result = result.filter((b) => {
      const brandSectorIds = b.sectors.map((s) => s.id)
      return sectorFilter.excluded.value.every((sectorId) => {
        if (sectorId === NO_SECTOR) return b.sectors.length > 0
        return !brandSectorIds.includes(sectorId)
      })
    })
  }

  // Filter by client (OR - must be subscribed by ANY selected org)
  if (selectedClients.value.length > 0) {
    result = result.filter((b) =>
      selectedClients.value.some((slug) => b.subscribed_org_slugs.includes(slug)),
    )
  }

  return result
})

// Platform filter options
const platformFilterOptions = computed(() =>
  rootPlatforms.value.map((p) => ({
    label: formatPlatformName(p.name),
    value: p.code,
  })),
)

// Sector filter options
const sectorFilterOptions = computed(() => [
  { label: 'No sector', value: NO_SECTOR },
  ...admin.sectors.map((s) => ({
    label: s.name,
    value: s.id,
    icon: s.emoji,
  })),
])

// Client (organization) filter options (selected items first)
const clientOptions = computed(() =>
  sortSelectedFirst(
    admin.organizations.map((o) => ({
      label: o.name,
      value: o.slug,
    })),
    selectedClients.value,
  ),
)

function clearClientFilter() {
  selectedClients.value = []
}

// Fetch all advertisers in one query
async function fetchAllAdvertisersData() {
  if (advertisersLoaded.value) return
  try {
    brandAdvertisersMap.value = await admin.fetchAllAdvertisers()
    advertisersLoaded.value = true
  } catch (err) {
    handleError(err, 'Failed to fetch advertisers')
  }
}

// Fetch all import stats in one query
async function fetchAllImportStats() {
  if (importStatsLoaded.value) return
  try {
    brandImportStatsMap.value = await imports.fetchAllBrandImportStats()
    importStatsLoaded.value = true
  } catch (err) {
    handleError(err, 'Failed to fetch import stats')
  }
}

// Initialize
onMounted(async () => {
  window.addEventListener('hashchange', handleHashChange)
  try {
    await Promise.all([
      admin.fetchBrands(),
      admin.fetchPlatforms(),
      admin.fetchCountries(),
      admin.fetchSectors(),
      admin.fetchOrganizations(),
    ])

    // Check for hash to open a specific brand
    const hash = window.location.hash.slice(1)
    if (hash) {
      const brand = admin.brands.find((b) => b.slug === hash)
      if (brand) {
        openBrandSlideover(brand)
      } else {
        handleError(new Error('Brand not found'), 'Could not find brand')
        history.replaceState(null, '', window.location.pathname)
      }
    }

    // Fetch all advertisers and import stats in parallel
    await Promise.all([fetchAllAdvertisersData(), fetchAllImportStats()])
  } catch (err) {
    handleError(err, 'Failed to load data')
  } finally {
    isLoading.value = false
  }
})

onUnmounted(() => {
  window.removeEventListener('hashchange', handleHashChange)
})

// Clear hash when slideover closes
watch(showBrandSlideover, (isOpen) => {
  if (!isOpen && window.location.hash) {
    history.replaceState(null, '', window.location.pathname)
  }
})

// Brand actions
async function handleCreateBrand(data: {
  name: string
  slug?: string
  website?: string | null
  logo_file?: File
}) {
  try {
    const brand = await admin.createBrand({
      name: data.name,
      slug: data.slug,
      website: data.website,
    })
    if (data.logo_file) {
      await admin.saveBrandLogo(brand.id, data.logo_file)
    }
    showCreateBrandModal.value = false
    const createdBrand = admin.brands.find((b) => b.id === brand.id)
    if (createdBrand) {
      openBrandSlideover(createdBrand)
    }
  } catch (err) {
    handleError(err, 'Failed to create brand')
  }
}

function openBrandSlideover(brand: Brand) {
  const isReplacing = showBrandSlideover.value
  selectedBrand.value = brand
  showBrandSlideover.value = true
  if (isReplacing) {
    history.replaceState(null, '', `#${brand.slug}`)
  } else {
    history.pushState(null, '', `#${brand.slug}`)
  }
}

function closeBrandSlideover() {
  showBrandSlideover.value = false
  selectedBrand.value = null
  if (window.location.hash) {
    history.replaceState(null, '', window.location.pathname)
  }
}

function handleHashChange() {
  const hash = window.location.hash.slice(1)
  if (!hash) {
    if (showBrandSlideover.value) {
      showBrandSlideover.value = false
      selectedBrand.value = null
    }
  } else if (selectedBrand.value?.slug !== hash) {
    const brand = admin.brands.find((b) => b.slug === hash)
    if (brand) {
      openBrandSlideover(brand)
    } else {
      handleError(new Error('Brand not found'), 'Could not find brand')
      history.replaceState(null, '', window.location.pathname)
    }
  }
}

function handleBrandDeleted() {
  closeBrandSlideover()
}

function handleBrandUpdated(brand: Brand) {
  selectedBrand.value = brand
}

function handleViewImports(brand: Brand) {
  router.push({ path: '/admin/imports', query: { search: brand.name } })
}

// Refresh data
async function refresh() {
  isLoading.value = true
  advertisersLoaded.value = false
  importStatsLoaded.value = false
  try {
    await Promise.all([
      admin.fetchBrands(),
      admin.fetchPlatforms(),
      admin.fetchCountries(),
      admin.fetchSectors(),
      admin.fetchOrganizations(),
    ])
    await Promise.all([fetchAllAdvertisersData(), fetchAllImportStats()])
  } catch (err) {
    handleError(err, 'Failed to refresh data')
  } finally {
    isLoading.value = false
  }
}
</script>

<template>
  <AppComponent title="Brands" id="admin-brands">
    <template #actions>
      <span class="text-sm text-muted">{{ admin.brands.length }} brands</span>
      <UButton
        icon="i-lucide-refresh-cw"
        color="neutral"
        variant="ghost"
        :loading="isLoading"
        @click="refresh"
      />
      <UButton icon="i-lucide-plus" color="primary" @click="showCreateBrandModal = true">
        Create Brand
      </UButton>
    </template>

    <div class="h-full flex flex-col">
      <!-- Filters -->
      <div class="p-4 space-y-2 shrink-0 bg-[var(--ui-bg)] border-b border-[var(--ui-border)]">
        <TriStateFilterButtons
          label="Filter by platform"
          :options="platformFilterOptions"
          :included="platformFilter.included.value"
          :excluded="platformFilter.excluded.value"
          @toggle="platformFilter.toggle"
          @clear="platformFilter.clear"
        />
        <TriStateFilterButtons
          label="Filter by sector"
          :options="sectorFilterOptions"
          :included="sectorFilter.included.value"
          :excluded="sectorFilter.excluded.value"
          @toggle="sectorFilter.toggle"
          @clear="sectorFilter.clear"
        />
        <div class="flex items-center gap-2">
          <span class="text-sm text-muted w-36 shrink-0">Filter by client</span>
          <USelectMenu
            v-model="selectedClients"
            :items="clientOptions"
            :ui="selectedClients.length ? activeFilterUi : undefined"
            size="sm"
            multiple
            placeholder="All clients"
            value-key="value"
            :filter-fields="['label']"
            class="w-44"
          />
          <UButton
            v-if="selectedClients.length"
            color="neutral"
            variant="ghost"
            size="xs"
            @click="clearClientFilter"
          >
            Clear
          </UButton>
        </div>
        <div class="flex items-center gap-2">
          <span class="text-sm text-muted w-36 shrink-0">Filter by name</span>
          <UInput
            v-model="searchQuery"
            icon="i-lucide-search"
            placeholder="Search brands..."
            :ui="searchQuery ? activeFilterUi : undefined"
            size="sm"
            class="w-64"
          />
        </div>
        <div class="text-sm text-muted">Showing {{ filteredBrands.length }} brands</div>
      </div>

      <!-- Brands Table -->
      <div class="flex-1 min-h-0 overflow-auto sticky-table-header">
        <BrandsTable
          :brands="filteredBrands"
          :brand-advertisers="brandAdvertisersMap"
          :brand-import-stats="brandImportStatsMap"
          :loading="isLoading"
          @settings="openBrandSlideover"
          @view-imports="handleViewImports"
        />
      </div>
    </div>

    <!-- Create Brand Modal -->
    <UModal v-model:open="showCreateBrandModal">
      <template #content>
        <UCard>
          <template #header>
            <h3 class="text-lg font-semibold">Create Brand</h3>
          </template>
          <AdminBrandForm
            :brand="null"
            @submit="handleCreateBrand"
            @cancel="showCreateBrandModal = false"
          />
        </UCard>
      </template>
    </UModal>

    <!-- Brand Management Slideover -->
    <USlideover v-model:open="showBrandSlideover" side="right">
      <template #content>
        <AdminBrandDetail
          :brand="selectedBrand"
          @close="closeBrandSlideover"
          @deleted="handleBrandDeleted"
          @updated="handleBrandUpdated"
        />
      </template>
    </USlideover>
  </AppComponent>
</template>
